import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Slider,
  TextField,
  Typography,
} from '@mui/material';
import { findLast, first, includes, map, range, size, slice, some, sortBy } from 'lodash';
import React, { useState } from 'react';

import { ClassificationBinningParams, MapValuesToBins } from 'interfaces/calculateFeatures';

interface ModelDetailsProps {
  classes?: string[];
  binning: ClassificationBinningParams;
  setBinning: (binning: ClassificationBinningParams) => void;
  onClosed?: () => void;
}

const NUM_BINS = 'num_bins';
const RANGE_BINS = 'range_bins';
const PARTIAL_RANGE_BINS = 'partial_range_bins';
const NONE = 'none';
const DEFAULT_NUM_BINS = 3;

const isPartialRangeBins = (mapValuesToBins: MapValuesToBins[], classes: string[]): boolean => {
  const sortedPartialRangeValue = sortBy(mapValuesToBins, 'minValue');
  const minValue = Number(first(classes)) ?? 1;
  const maxValue = size(classes);

  // Check if the first bin starts at the minimum value
  if (sortedPartialRangeValue[0].minValue !== minValue) {
    return true;
  }

  // Check if the last bin ends at the maximum value
  if (sortedPartialRangeValue[size(sortedPartialRangeValue) - 1].maxValue !== maxValue) {
    return true;
  }

  // Check if there are any gaps between bins
  for (let i = 1; i < size(sortedPartialRangeValue); i++) {
    if (sortedPartialRangeValue[i].minValue > sortedPartialRangeValue[i - 1].maxValue) {
      return true;
    }
  }

  return false;
};

const isValidPartialRange = (
  partialRangeValue: { minValue?: number; maxValue?: number }[],
  classes: string[]
): boolean => {
  if (size(partialRangeValue) === 0) {
    return false; // No bins defined
  }

  const minValue = Number(first(classes)) ?? 1;
  const maxValue = size(classes);

  // Check if all bins have valid min and max values
  if (
    some(
      partialRangeValue,
      (value) =>
        value.minValue === undefined ||
        value.maxValue === undefined ||
        (value.minValue !== undefined && (value.minValue < minValue || value.minValue > maxValue)) ||
        (value.maxValue !== undefined && (value.maxValue < minValue || value.maxValue > maxValue)) ||
        (value.minValue !== undefined && value.maxValue !== undefined && value.minValue >= value.maxValue)
    )
  ) {
    return false;
  }

  // check if there is not overlap between all bins
  if (size(partialRangeValue) === 1) {
    // If there's only one bin, it cannot overlap with anything else
    return true;
  }

  const sortedPartialRangeValue = sortBy(partialRangeValue, 'minValue');
  const hasOverlap = some(
    range(1, size(sortedPartialRangeValue)),
    (i) =>
      sortedPartialRangeValue[i].minValue !== undefined &&
      sortedPartialRangeValue[i - 1].maxValue !== undefined &&
      sortedPartialRangeValue[i].minValue <= sortedPartialRangeValue[i - 1].maxValue
  );

  if (hasOverlap) {
    return false; // Overlap found
  }

  return true;
};

export const IntensityBins: React.FC<React.PropsWithChildren<ModelDetailsProps>> = ({
  classes = map(range(0, 255), String),
  binning,
  onClosed,
  setBinning,
}) => {
  const [binsOptions, setBinsOption] = useState<string>(
    binning.mapValuesToBins
      ? isPartialRangeBins(binning.mapValuesToBins, classes)
        ? PARTIAL_RANGE_BINS
        : RANGE_BINS
      : binning.numBins
      ? NUM_BINS
      : NONE
  );
  const defaultNumBins = size(classes) > DEFAULT_NUM_BINS ? DEFAULT_NUM_BINS : null;
  const [numBins, setNumBins] = useState<number>(binning.numBins ?? defaultNumBins);
  const [sliderRange, setSliderRange] = useState(
    binning.mapValuesToBins ? map(slice(binning.mapValuesToBins, 0, -1), 'maxValue') : [size(classes) / 2]
  );
  const [partialRangeValue, setPartialRangeValue] = useState(binning.mapValuesToBins || []);

  const minValue = Number(first(classes)) ?? 1;

  const mapValuesToBins = map(sliderRange, (bin, index) => ({
    bin: index + 1,
    minValue: index === 0 ? minValue : sliderRange[index - 1],
    maxValue: bin,
  }));

  mapValuesToBins.push({
    bin: size(sliderRange) + 1,
    minValue: sliderRange[size(sliderRange) - 1],
    maxValue: size(classes),
  });

  const marks = map(classes, (intensityClass, index) => ({
    value: index + 1,
    label: index + 1,
  }));

  return (
    <Grid container p={2} direction="column" spacing={2} flexWrap="nowrap">
      <Grid container item alignItems="center">
        <Grid item xs={10}>
          <Typography variant="h3">Bins</Typography>
        </Grid>
        <Grid item xs={2} textAlign="right">
          <IconButton onClick={onClosed}>
            <CloseIcon />
          </IconButton>
        </Grid>
      </Grid>

      <Grid item>
        <FormControl>
          <RadioGroup
            value={binsOptions}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBinsOption(e.target.value)}
          >
            <FormControlLabel value={NUM_BINS} control={<Radio />} label="Num bins" />
            <FormControlLabel value={RANGE_BINS} control={<Radio />} label="Range bins" />
            <FormControlLabel value={PARTIAL_RANGE_BINS} control={<Radio />} label="Partial range bins" />
            <FormControlLabel value={NONE} control={<Radio />} label="None" />
          </RadioGroup>
        </FormControl>
      </Grid>

      {binsOptions === NUM_BINS ? (
        <Grid item>
          <TextField
            label="Number of bins"
            required
            value={numBins}
            type="number"
            inputProps={{
              max: size(classes),
              min: 1,
            }}
            onChange={(event) => {
              let value = Number(event.target.value);
              if (value > size(classes)) {
                value = size(classes);
              }
              setNumBins(Number(value));
            }}
          />
        </Grid>
      ) : binsOptions === RANGE_BINS ? (
        <Grid item container direction="column">
          <Grid item container xs={12} spacing={1}>
            <Grid item xs={8}>
              <Slider
                min={minValue}
                max={size(classes)}
                step={0.01}
                track={false}
                aria-labelledby="track-false-range-slider"
                marks={size(marks) > 10 ? undefined : marks}
                value={sliderRange}
                onChange={(event, value) => {
                  setSliderRange(value as number[]);
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <IconButton
                disabled={size(sliderRange) === size(classes)}
                onClick={() =>
                  setSliderRange((prev: number[]) => {
                    // find the biggest value that is not in the current range to add
                    const biggestValue = findLast(marks, (mark) => !includes(prev, mark.value))?.value;
                    if (biggestValue) {
                      return [...prev, biggestValue];
                    }
                  })
                }
              >
                <AddIcon />
              </IconButton>
              <IconButton
                disabled={size(sliderRange) === 1}
                onClick={() =>
                  setSliderRange((prev: number[]) => {
                    return slice(prev, 0, -1);
                  })
                }
              >
                <RemoveIcon />
              </IconButton>
            </Grid>
          </Grid>
          {map(mapValuesToBins, (bin, index) => (
            <Grid item container xs={12} spacing={1} key={index}>
              <Grid item>{bin.bin} :</Grid>
              <Grid item>
                {bin.minValue} {'<= value <'}
                {index === size(mapValuesToBins) - 1 ? '=' : ''} {bin.maxValue}
              </Grid>
            </Grid>
          ))}
        </Grid>
      ) : binsOptions === PARTIAL_RANGE_BINS ? (
        <>
          {map(partialRangeValue, (value, index) => (
            <Grid item container xs={12} spacing={1} key={index} direction="row">
              <Grid item xs={5}>
                <TextField
                  size="small"
                  label={`Min value for bin ${index + 1}`}
                  required
                  value={value.minValue ?? ''}
                  type="number"
                  inputProps={{
                    max: size(classes),
                    min: minValue,
                  }}
                  error={
                    value.minValue < minValue ||
                    value.minValue > size(classes) ||
                    (value.maxValue !== undefined && value.minValue >= value.maxValue)
                  }
                  onChange={(event) => {
                    const newValue = Number(event.target.value);
                    setPartialRangeValue((prev) => {
                      const updated = [...prev];
                      updated[index] = { ...updated[index], minValue: newValue };
                      return updated;
                    });
                  }}
                />
              </Grid>
              <Grid item xs={5}>
                <TextField
                  size="small"
                  label={`Max value for bin ${index + 1}`}
                  required
                  value={value.maxValue ?? ''}
                  type="number"
                  inputProps={{
                    max: size(classes),
                    min: minValue,
                  }}
                  error={
                    value.maxValue < minValue ||
                    value.maxValue > size(classes) ||
                    (value.minValue !== undefined && value.maxValue <= value.minValue)
                  }
                  onChange={(event) => {
                    const newValue = Number(event.target.value);
                    setPartialRangeValue((prev) => {
                      const updated = [...prev];
                      updated[index] = { ...updated[index], maxValue: newValue };
                      return updated;
                    });
                  }}
                />
              </Grid>
            </Grid>
          ))}
          <Grid item container xs={12} spacing={1}>
            <IconButton
              onClick={() => {
                setPartialRangeValue((prev) => [...prev, { minValue: undefined, maxValue: undefined, bin: undefined }]);
              }}
            >
              <AddIcon />
            </IconButton>
            <IconButton
              onClick={() => {
                setPartialRangeValue((prev) => {
                  if (size(prev) > 1) {
                    return slice(prev, 0, -1);
                  }
                  return prev; // Do not remove the last item
                });
              }}
            >
              <RemoveIcon />
            </IconButton>
          </Grid>
        </>
      ) : null}
      <Grid item>
        <Button
          variant="contained"
          color="primary"
          disabled={
            (binsOptions === NUM_BINS && !numBins) ||
            (binsOptions === RANGE_BINS && size(sliderRange) < 2) ||
            (binsOptions === PARTIAL_RANGE_BINS && !isValidPartialRange(partialRangeValue, classes))
          }
          onClick={() => {
            if (binsOptions === NUM_BINS) {
              setBinning({ numBins });
            } else if (binsOptions === RANGE_BINS) {
              setBinning({ mapValuesToBins });
            } else if (binsOptions === PARTIAL_RANGE_BINS) {
              const mapValuesToBinsPartialRange: MapValuesToBins[] = map(
                sortBy(partialRangeValue, 'minValue'),
                (value, index) => ({
                  bin: index + 1,
                  minValue: value.minValue,
                  maxValue: value.maxValue,
                })
              );
              setBinning({ mapValuesToBins: mapValuesToBinsPartialRange });
            } else {
              setBinning({ mapValuesToBins: null, numBins: null });
            }

            onClosed();
          }}
        >
          Save
        </Button>
      </Grid>
    </Grid>
  );
};
